import React, { useEffect, useState } from "react";
import { Drawer } from "@mantine/core";
import moment from "moment";
import swal from "sweetalert";
import Icon from "./icons";
import schedulerService from "../../../services/scheduler";
import { SelectDD, CDatePicker } from "./CreateShiftDrawer";
import "./SchedulerCreate.css";

// Staff "Request Time Off" form. Reuses the established Scheduler drawer chrome
// (sd-root/head/body/foot), custom SelectDD + SingleDatePicker (no native
// pickers), and the fixed footer — matching the Add Schedule drawer.
//
// `RequestTimeOffForm` is the bare panel content (head/body/foot). It can render
// standalone inside its own <Drawer> (the Time Off tab) OR be swapped into an
// already-open drawer in place of other content (from a shift's detail panel),
// which is why it does not own the <Drawer> itself.
const TYPES = [
  { value: "pto", label: "PTO" },
  { value: "medical", label: "Medical leave" },
  { value: "unpaid", label: "Unpaid leave" },
];
const REASON_OPTS = ["Vacation", "Personal", "Family / childcare", "Medical", "Appointment", "Illness", "Bereavement", "Jury duty", "Other"].map((r) => ({ value: r, label: r }));

export function RequestTimeOffForm({ active = true, onClose, onBack, prefillStart = "", onSubmitted }) {
  const [type, setType] = useState("pto");
  // One or more specific days off — a continuous range or several non-continuous
  // days. Each entry is a local "YYYY-MM-DD" string (the picker's format).
  const [dates, setDates] = useState([]);
  const [reasonSel, setReasonSel] = useState("Vacation");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => { schedulerService.getSchedulerSettings().then((s) => setSettings(s || null)).catch(() => {}); }, []);
  useEffect(() => {
    if (active) { setType("pto"); setDates(prefillStart ? [prefillStart] : []); setReasonSel("Vacation"); setDesc(""); }
  }, [active, prefillStart]);

  const minNotice = settings?.rules?.timeOffMinNoticeDays ?? 0;
  const earliest = dates.length ? [...dates].sort()[0] : "";
  const noticeWarning = earliest && minNotice && moment(earliest).isBefore(moment().startOf("day").add(minNotice, "days"))
    ? `Your organization asks for at least ${minNotice} days' notice. This request starts sooner, so it may be harder to approve.`
    : null;

  const submit = async () => {
    if (!dates.length) { swal("Date required", "Please choose at least one date.", "info"); return; }
    const d = desc.trim();
    const reason = d ? `${reasonSel}: ${d}` : reasonSel;
    setBusy(true);
    await schedulerService.createTimeOff({ type, dates, reason }, () => {});
    setBusy(false);
    onSubmitted?.();
    onClose?.();
  };

  return (
    <div className="sd-root">
      <div className="sd-head">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {onBack ? (
            <button type="button" className="sd-close" onClick={onBack} aria-label="Back to shift">
              <Icon name="chevronLeft" size={16} />
            </button>
          ) : null}
          <h3>Request Time Off</h3>
        </div>
        <button type="button" className="sd-close" onClick={onClose} aria-label="Close">
          <Icon name="x" size={16} />
        </button>
      </div>

      <div className="sd-body sd-create">
        <div className="field">
          <label>Type</label>
          <SelectDD placeholder="Select type" value={type} options={TYPES} onChange={(v) => setType(v || "pto")} />
        </div>
        <div className="field">
          <label>Date(s)</label>
          <CDatePicker dates={dates} onChange={setDates} isAppt multi placeholder="Select date(s)" />
          <div style={{ fontSize: 11.5, color: "var(--app-text-muted)", marginTop: 6 }}>
            Pick a single day, a range, or several separate days.
          </div>
        </div>
        <div className="field">
          <label>Reason</label>
          <SelectDD placeholder="Select reason" value={reasonSel} options={REASON_OPTS} onChange={(v) => setReasonSel(v || "Vacation")} />
        </div>
        <div className="field">
          <label>Description (optional)</label>
          <textarea className="inp" style={{ height: "auto", minHeight: 90, padding: "10px 12px", resize: "vertical" }} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Add any details for your manager…" />
        </div>
        {noticeWarning ? (
          <div style={{ fontSize: 12.5, color: "#b45309", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px", display: "flex", gap: 6, alignItems: "flex-start" }}>
            <Icon name="alert" size={13} /> <span>{noticeWarning}</span>
          </div>
        ) : null}
      </div>

      <div className="sd-foot">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
        <span className="spacer" />
        <button type="button" className="btn btn-primary btn-sm" onClick={submit} disabled={busy || !dates.length}>
          {busy ? "Submitting…" : "Submit request"}
        </button>
      </div>
    </div>
  );
}

// Standalone drawer wrapper — opened from the Time Off tab.
export default function RequestTimeOffDrawer({ opened, onClose, onBack, prefillStart = "", onSubmitted }) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={460}
      withCloseButton={false}
      padding={0}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
      zIndex={1100}
    >
      <RequestTimeOffForm active={opened} onClose={onClose} onBack={onBack} prefillStart={prefillStart} onSubmitted={onSubmitted} />
    </Drawer>
  );
}
